import siteStats from '@data/site-stats';
import Head from 'next/head';
import config from '@/config';
import i18n from '@/i18n.json';
import company from '@/lib/company';

const ORGANIZATION_ID = `${company.url}/#organization`;

// The descriptions below are English in every locale, and so are their numbers.
const stats = siteStats.labels(i18n.defaultLocale);

/**
 * JSON-LD structured data components for SEO
 * Implements schema.org markup for rich search results
 */

/**
 * JSON for a <script> tag, used by every schema below. Issuer-supplied text
 * (brand names, redemption methods, descriptions) can contain anything, so "<"
 * is escaped: a stray "</script>" must not be able to close the tag.
 */
export const serializeJsonLd = (schema) => JSON.stringify(schema).replace(/</g, '\\u003c');

/**
 * Organization schema - used site-wide in DefaultHead.
 * Same entity (and @id) as the marketing site's Organization, so search
 * engines merge the two into one company record.
 */
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: company.name,
    legalName: company.legalName,
    url: company.url,
    logo: company.logo,
    description: `Bidali is a gift card company. Its store sells gift cards from ${stats.brandCount} brands in ${stats.countryCount} countries, delivered by email.`,
    foundingDate: String(company.foundedYear),
    telephone: company.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: company.address.street,
      addressLocality: company.address.locality,
      addressRegion: company.address.region,
      postalCode: company.address.postalCode,
      addressCountry: company.address.countryCode,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: config.supportEmail,
      url: config.supportUrl,
    },
    sameAs: Object.values(company.socials),
  };

  return (
    <Head>
      <script
        key="organization-jsonld"
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
      />
    </Head>
  );
};

/**
 * WebSite schema with SearchAction for sitelinks searchbox
 * Used on homepage
 */
export const WebSiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${config.rootUrl}/#website`,
    name: 'Bidali gift cards',
    alternateName: 'Bidali',
    url: config.rootUrl,
    description: `Gift cards from ${stats.brandCount} brands in ${stats.countryCount} countries, delivered by email.`,
    publisher: { '@id': ORGANIZATION_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${config.rootUrl}/buy?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Head>
      <script
        key="website-jsonld"
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
      />
    </Head>
  );
};

/**
 * Product schema for brand detail pages. A plain formatter: the page derives
 * every value from the brand record (see components/brand/brand-content.js).
 * No review or rating markup: we have none to show.
 *
 * @param {Object} props
 * @param {string} props.name - Product name, as in the page h1
 * @param {string} [props.description] - Plain-text description
 * @param {string} [props.image] - Brand image URL
 * @param {string} props.url - Canonical URL of the page
 * @param {string} props.brandName - Brand name
 * @param {string} [props.category] - Category name
 * @param {string} [props.countryName] - Country the product works in
 * @param {Object} props.prices - getPriceSummary() result; no schema without it
 * @param {boolean} props.inStock - Whether it can be bought right now
 */
export const ProductSchema = ({
  name,
  description,
  image,
  url,
  brandName,
  category,
  countryName,
  prices,
  inStock,
}) => {
  if (!name || !url || !prices) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    ...(description && { description }),
    ...(image && { image }),
    url,
    brand: {
      '@type': 'Brand',
      name: brandName,
    },
    ...(category && { category }),
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: prices.currency,
      lowPrice: Number((prices.lowInCents / 100).toFixed(2)),
      highPrice: Number((prices.highInCents / 100).toFixed(2)),
      offerCount: prices.offerCount,
      availability: inStock ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
      url,
      ...(countryName && {
        eligibleRegion: {
          '@type': 'Country',
          name: countryName,
        },
      }),
    },
  };

  return (
    <Head>
      <script
        key="product-jsonld"
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
      />
    </Head>
  );
};

/**
 * BreadcrumbList schema for navigation hierarchy
 * Improves SERP display with breadcrumb trails
 * @param {Array} items - Array of {name, url} objects, in the order of the visible breadcrumb
 */
export const BreadcrumbSchema = ({ items }) => {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Head>
      <script
        key="breadcrumb-jsonld"
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
      />
    </Head>
  );
};

/**
 * FAQ schema for FAQ pages or sections
 * Can generate rich FAQ snippets in search results
 * @param {Array} faqs - Array of {question, answer} objects
 */
export const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Head>
      <script
        key="faq-jsonld"
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Required for JSON-LD
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
      />
    </Head>
  );
};
